import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx / Urls.jsx / WordpressSync.jsx.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  success: "#2F9E44",
  successSoft: "#E9F7EC",
  danger: "#E0393B",
  dangerSoft: "#FCEAEA",
  warning: "#E8890C",
  warningSoft: "#FDF1E1",
};
const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const FONT_IMPORT = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
`;

/* Centralized so a wrong guess about a route is a one-line
   fix. Note: the wordpress-sync-logs path had an accidental
   double slash in the original spec ("sitemap//wordpress")
   — normalized to a single slash here; change it back if
   that was intentional on your backend. */
const ENDPOINTS = {
  activity: "/api/v1/sitemap/activity-logs",
  wordpressSync: "/api/v1/sitemap/wordpress/logs",
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

function useDebouncedValue(value, delayMs = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/* ---------------------------------------------------------
   Shared server-table state: search (debounced), pagination,
   loading, and fetch — reused by both tabs so the request/
   pagination logic only exists once.
--------------------------------------------------------- */
function useLogTable(endpoint, active) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 400);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(endpoint, {
          page: page + 1,
          limit: rowsPerPage,
          search,
        });
        if (cancelled) return;
        setRows(data.data || []);
        setTotalRows(data.pagination?.total || 0);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || "Failed to fetch logs.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [endpoint, active, page, rowsPerPage, search]);

  return {
    rows,
    loading,
    totalRows,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchInput,
    setSearchInput,
  };
}

function StatusChip({ label, tone }) {
  const palette = {
    success: [COLOR.successSoft, COLOR.success],
    danger: [COLOR.dangerSoft, COLOR.danger],
    warning: [COLOR.warningSoft, COLOR.warning],
    accent: [COLOR.accentSoft, COLOR.accent],
    neutral: [COLOR.bg, COLOR.inkMuted],
  }[tone] || [COLOR.bg, COLOR.inkMuted];

  return (
    <Chip
      size="small"
      label={label}
      sx={{ bgcolor: palette[0], color: palette[1], fontWeight: 500, fontSize: "12px" }}
    />
  );
}

function severityTone(severity) {
  switch (severity?.toLowerCase()) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "danger";
    default:
      return "accent";
  }
}

function syncStatusTone(status) {
  switch (status?.toLowerCase()) {
    case "success":
    case "completed":
      return "success";
    case "failed":
    case "error":
      return "danger";
    case "running":
    case "in_progress":
      return "accent";
    case "partial":
      return "warning";
    default:
      return "neutral";
  }
}

function formatDuration(startedAt, completedAt, durationSeconds) {
  let totalSeconds = null;

  if (durationSeconds !== null && durationSeconds !== undefined) {
    totalSeconds = Number(durationSeconds);
  } else if (startedAt && completedAt) {
    const ms = new Date(completedAt) - new Date(startedAt);
    if (Number.isFinite(ms) && ms >= 0) totalSeconds = ms / 1000;
  }

  if (totalSeconds === null || !Number.isFinite(totalSeconds)) return null;

  const rounded = Math.round(totalSeconds);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/* ---------------------------------------------------------
   Shared chrome: search box + table + pagination. Each tab
   supplies its own columns and row renderer.
--------------------------------------------------------- */
function LogTablePanel({ table, columns, renderRow, searchPlaceholder, emptyLabel }) {
  const hasRows = table.rows.length > 0;

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "10px", border: `1px solid ${COLOR.line}` }}>
        <TextField
          fullWidth
          size="small"
          placeholder={searchPlaceholder}
          value={table.searchInput}
          onChange={(e) => table.setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: COLOR.inkFaint }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${COLOR.line}`, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: COLOR.bg } }}>
                {columns.map((col) => (
                  <TableCell
                    key={col.label}
                    align={col.align || "left"}
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: COLOR.inkFaint,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      borderBottom: `1px solid ${COLOR.line}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {table.loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={26} sx={{ color: COLOR.accent }} />
                  </TableCell>
                </TableRow>
              ) : !hasRows ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: 0 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                      <InboxOutlinedIcon sx={{ fontSize: 28, color: COLOR.inkFaint }} />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: COLOR.ink }}>
                        {table.searchInput ? "No results match your search" : emptyLabel}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                table.rows.map(renderRow)
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={table.totalRows}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={(e, newPage) => table.setPage(newPage)}
          onRowsPerPageChange={(e) => {
            table.setRowsPerPage(parseInt(e.target.value, 10));
            table.setPage(0);
          }}
          sx={{ borderTop: `1px solid ${COLOR.line}` }}
        />
      </Paper>
    </>
  );
}

/* =========================================================
   Main component
========================================================= */
export default function SitemapLogs() {
  const [tab, setTab] = useState(0);

  const activityTable = useLogTable(ENDPOINTS.activity, tab === 0);
  const wordpressTable = useLogTable(ENDPOINTS.wordpressSync, tab === 1);

  const activityColumns = useMemo(
    () => [
      { label: "Date" },
      { label: "Module" },
      { label: "Action" },
      { label: "Severity" },
      { label: "Description" },
      { label: "User" },
    ],
    []
  );

  const wordpressColumns = useMemo(
    () => [
      { label: "Started" },
      { label: "Group" },
      { label: "Status" },
      { label: "Records", align: "center" },
      { label: "Duration", align: "center" },
      { label: "Triggered by" },
    ],
    []
  );

  return (
    <Box>
      <style>{FONT_IMPORT}</style>

      <Typography
        sx={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: "20px", color: COLOR.ink, mb: 2.5 }}
      >
        Sitemap Logs
      </Typography>

      <Tabs
        value={tab}
        onChange={(e, newValue) => setTab(newValue)}
        sx={{
          mb: 3,
          minHeight: "40px",
          borderBottom: `1px solid ${COLOR.line}`,
          "& .MuiTabs-indicator": { bgcolor: COLOR.accent, height: "2.5px" },
        }}
      >
        <Tab
          icon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Activity Logs"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13.5px",
            minHeight: "40px",
            color: COLOR.inkMuted,
            "&.Mui-selected": { color: COLOR.accent },
          }}
        />
        <Tab
          icon={<SyncOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="WordPress Sync Logs"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13.5px",
            minHeight: "40px",
            color: COLOR.inkMuted,
            "&.Mui-selected": { color: COLOR.accent },
          }}
        />
      </Tabs>

      {tab === 0 && (
        <LogTablePanel
          table={activityTable}
          columns={activityColumns}
          searchPlaceholder="Search activity…"
          emptyLabel="No activity found"
          renderRow={(log) => (
            <TableRow key={log.id} hover sx={{ "& td": { borderBottom: `1px solid ${COLOR.line}` } }}>
              <TableCell sx={{ fontFamily: FONT_MONO, fontSize: "12.5px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                {new Date(log.createdAt).toLocaleString()}
              </TableCell>
              <TableCell sx={{ fontSize: "13px", fontWeight: 500, color: COLOR.ink, whiteSpace: "nowrap" }}>
                {log.module}
              </TableCell>
              <TableCell sx={{ fontSize: "13px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                {log.action}
              </TableCell>
              <TableCell>
                <StatusChip label={log.severity || "info"} tone={severityTone(log.severity)} />
              </TableCell>
              <TableCell sx={{ fontSize: "13px", color: COLOR.ink, minWidth: 220 }}>
                {log.description}
              </TableCell>
              <TableCell sx={{ fontSize: "13px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                {log.user ?? "—"}
              </TableCell>
            </TableRow>
          )}
        />
      )}

      {tab === 1 && (
        <LogTablePanel
          table={wordpressTable}
          columns={wordpressColumns}
          searchPlaceholder="Search sync logs…"
          emptyLabel="No sync activity yet"
          renderRow={(log) => {
            const duration = formatDuration(log.started_at, log.completed_at, log.duration_seconds);
            const tone = syncStatusTone(log.status);
            const isRunning = !log.completed_at && tone === "accent";
            const hasFailures = (log.failed_records || 0) > 0;

            return (
              <TableRow key={log.id} hover sx={{ "& td": { borderBottom: `1px solid ${COLOR.line}` } }}>
                <TableCell sx={{ fontFamily: FONT_MONO, fontSize: "12.5px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                  {log.started_at ? new Date(log.started_at).toLocaleString() : "—"}
                </TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: COLOR.ink }}>
                    {log.group?.name || "All groups"}
                  </Typography>
                  {log.group?.slug && (
                    <Typography sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLOR.inkFaint }}>
                      {log.group.slug}.xml
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <StatusChip
                    label={isRunning ? "Running…" : log.status || "unknown"}
                    tone={tone}
                  />
                </TableCell>

                <TableCell align="center">
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.inkMuted }}>
                    {log.synced_records ?? 0}/{log.total_records ?? 0}
                  </Typography>
                  {hasFailures && (
                    <Typography sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: COLOR.danger }}>
                      {log.failed_records} failed
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="center" sx={{ fontFamily: FONT_MONO, fontSize: "12.5px", color: COLOR.inkMuted }}>
                  {duration || (isRunning ? "—" : "—")}
                </TableCell>

                <TableCell sx={{ fontSize: "13px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                  {log.user_email ?? "system"}
                  {log.message && (
                    <Tooltip title={log.message}>
                      <Box component="span" sx={{ color: COLOR.inkFaint, ml: "6px", cursor: "help" }}>
                        ⓘ
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          }}
        />
      )}
    </Box>
  );
}